import { TileTextureProviderComponentBase } from '@/stellar-globe-components';
import { ImageFilter } from '@/utils/image_filter';
import { Globe, tile } from "@hscmap/stellar-globe";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { FitsImage } from '../FitsImage';
import { Tone } from '../tone';
import { store } from '@/store';

type Params = any

@Component
export default class TextureProviderComponent extends TileTextureProviderComponentBase<TextureProvider> {
  @Prop({ required: true })
  fitsImage!: FitsImage

  created() {
    this.$watch(() => this.fitsImage.tone.type, () => {
      this.tp.setImageFilter()
      this.updateParams(true)
    })
    this.$watch(() => this.fitsImage.tone, () => {
      this.updateParams()
    }, { deep: true })
    this.$watch(() => store.config.tileLayer.enableMagFilter, () => {
      this.updateParams(true)
    })
  }

  newTextureProvider() {
    return new TextureProvider(this.globe, this)
  }
}

class LinearMixer extends ImageFilter {
  constructor(gl: WebGLRenderingContext, readonly fitsImage: FitsImage) {
    super(gl)
  }

  get tone() {
    return this.fitsImage.tone.linear
  }

  fragmentShader() {
    return require('webpack-glsl-loader!./glsl/linear.glsl') as string
  }

  setUniformParams() {
    this.program.uniform1f({
      u_bias: this.tone.bias,
      u_contrast: this.tone.contrast,
    })
  }
}

class AsinhMixer extends ImageFilter {
  constructor(gl: WebGLRenderingContext, readonly fitsImage: FitsImage) {
    super(gl)
  }

  get tone() {
    return this.fitsImage.tone.asinh
  }

  fragmentShader() {
    return require('webpack-glsl-loader!./glsl/asinh.glsl') as string
  }

  setUniformParams() {
    this.program.uniform1f({
      u_beta: this.tone.beta,
      u_b0: this.tone.b0,
      u_a: this.tone.a,
      u_bias: this.tone.bias,
    })
  }
}

export class TextureProvider extends tile.AsyncTextureProvider {
  readonly tract: tile.Tract
  readonly fitsImage: FitsImage
  private imageFilter!: ImageFilter

  constructor(globe: Globe, readonly ttc: TextureProviderComponent) {
    super(globe)
    this.fitsImage = ttc.fitsImage
    this.tract = tile.Tract.fromFitsHeader(this.fitsImage.hdu.header)
    this.setImageFilter()
  }

  setImageFilter() {
    this.imageFilter && this.imageFilter.release()
    switch (this.fitsImage.tone.type) {
      case 'linear':
        this.imageFilter = new LinearMixer(this.globe.gl, this.fitsImage)
        break
      case 'asinh':
        this.imageFilter = new AsinhMixer(this.globe.gl, this.fitsImage)
        break
    }
  }

  release() {
    this.imageFilter.release()
    super.release()
  }

  walkTracts(cb: (tract: tile.Tract) => void) {
    cb(this.tract)
  }

  async makeTileTexture(ref: tile.TileRef) {
    const tt = new tile.TileTexture(this)
    const gl = this.globe.gl
    const { tileSize } = ref.tract

    if (this.alreadyReleased) {
      return tt
    }

    const path = `${ref.level}/${ref.p}/${ref.q}`
    const ab = this.ttc.fitsImage.tiles![path]
    if (ab === undefined) {
      throw new Error(`no tile ${path}`)
    }

    this.imageFilter.apply(tt.tex, tileSize, tileSize, 1, (t) => {
      t.bind(() => {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
        gl.texImage2D(
          gl.TEXTURE_2D, 0, gl.LUMINANCE, tileSize, tileSize, 0, gl.LUMINANCE, gl.FLOAT, new Float32Array(ab))
      })
    })

    tt.tex.bind(() => {
      if (ref.level === ref.tract.maxTileLevel) {
        gl.generateMipmap(gl.TEXTURE_2D)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR)
      }
      if (!this.ttc.Store.config.tileLayer.enableMagFilter && ref.level === 0) {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
      }
    })
    return tt
  }
}
